const tips = {
  English: [
    {
      heading: "Read the question first",
      body: "Before diving into the passage, read the question stem to know what you're looking for. This makes your reading faster and more focused.",
    },
    {
      heading: "All answers come from the text",
      body: "Every correct answer is directly supported by the passage. Never use outside knowledge or personal opinion — always find proof in the text.",
    },
    {
      heading: "Evidence-based pairs",
      body: "For claim + evidence two-part questions, your evidence choice must directly support your claim choice. Solve them together, not independently.",
    },
    {
      heading: "Vocabulary in context",
      body: "For word/phrase meaning questions, substitute each answer choice back into the sentence. Choose the meaning that fits the surrounding context, not the most common definition.",
    },
    {
      heading: "Eliminate half-right choices",
      body: "A choice that is partially correct is still wrong. Eliminate any answer that goes too far, contradicts the passage, or introduces ideas not mentioned.",
    },
    {
      heading: "Pacing: ~1 min 15 sec per question",
      body: "If you're stuck on a question, mark it and move on. Return at the end — spending too long on one question costs you others.",
    },
  ],
  Math: [
    {
      heading: "Understand before you solve",
      body: "Read carefully and identify what's being asked. Write out key values and unknowns before solving — it reduces errors and clarifies your approach.",
    },
    {
      heading: "Use the Reference Sheet",
      body: "Don't memorize formulas. The reference sheet has the key geometric and algebraic formulas — use it freely throughout the section.",
    },
    {
      heading: "Calculator: tool, not shortcut",
      body: "Set up the problem mentally first. Use the calculator for computation, not to figure out what operation to perform.",
    },
    {
      heading: "Plug in answer choices",
      body: "For multiple-choice, if algebra feels complex, try substituting the answer choices back into the problem. Start with the middle value to narrow options quickly.",
    },
    {
      heading: "Watch units and labels",
      body: "Many errors come from misreading units (hours vs. minutes, dollars vs. cents). Always re-read what the final answer should represent.",
    },
    {
      heading: "Free-response entry rules",
      body: "You can enter fractions, decimals, or whole numbers. Enter the most exact form. Negative answers are valid. Truncate or round only when the problem requires it.",
    },
  ],
};

const DirectionModal = ({ onClose, mode }) => {
  const sections = tips[mode] || tips.English;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-[#0d0f1a]/98 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-t-2xl" />

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-3 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold bg-gradient-to-r from-blue-300 to-indigo-400 bg-clip-text text-transparent">
              SAT {mode} — Key Strategies
            </h2>
            <p className="text-xs text-white/35 mt-1">
              {mode === "English"
                ? "Reading & Writing · 54 questions · 64 minutes"
                : "Math · 44 questions · 70 minutes"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="bg-white/[0.07] hover:bg-white/[0.14] rounded-lg p-1.5 text-white/50 hover:text-white transition-all duration-200 flex-shrink-0 ml-4 mt-0.5 border border-white/10"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="h-px bg-white/[0.07] mx-5" />

        {/* Scrollable tips */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3 scrollbar-thin scrollbar-thumb-white/15 scrollbar-track-transparent">
          {sections.map((tip, i) => (
            <div
              key={i}
              className="relative overflow-hidden bg-white/[0.04] border border-white/[0.07] rounded-xl p-3.5"
            >
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />
              <div className="flex items-start gap-3">
                <span className="shrink-0 w-5 h-5 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-[10px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <div>
                  <p className="text-sm font-semibold text-white/85 mb-1">{tip.heading}</p>
                  <p className="text-xs text-white/45 leading-relaxed">{tip.body}</p>
                </div>
              </div>
            </div>
          ))}

          {mode === "Math" && (
            <div className="relative overflow-hidden bg-white/[0.03] border border-white/[0.06] rounded-xl p-3.5 mt-1">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />
              <p className="text-[10px] font-semibold text-white/30 uppercase tracking-wider mb-2">Unless otherwise indicated</p>
              <ul className="space-y-1 text-xs text-white/40 leading-relaxed list-disc list-inside">
                <li>All variables and expressions represent real numbers.</li>
                <li>Figures provided are drawn to scale.</li>
                <li>All figures lie in a plane.</li>
                <li>The domain of a function f is all real x for which f(x) is a real number.</li>
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/[0.07] flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2 rounded-xl text-sm font-semibold bg-blue-500/15 border border-blue-400/25 text-blue-300 hover:bg-blue-500/25 transition-all duration-200"
          >
            Got it — back to the test
          </button>
        </div>
      </div>
    </div>
  );
};

export default DirectionModal;
